import React, { useState } from 'react';
import { sortableElement } from 'react-sortable-hoc';
import { Divider, Tooltip, Switch } from 'antd';
import { number, func, object } from 'prop-types';
import { get, map, filter, find, flatMap, size, findIndex, includes, some, times, first } from 'lodash';
import styled from 'styled-components';
import { Icon, FormInput, FormSelect, Column, Row, FlexContainer, Text } from '../../../../common';
import MultipleOptions from './MultipleOptions';
import { typeQuestionOptions, typeQuestions, satisfactionOptions, addUUID, genUUID } from '../../../../../utils/misc';
import ConditionalQuestion from './ConditionalQuestion';
import Nps from './Nps';

const WrapperIcon = styled(FlexContainer)`
    border: 1px solid ${({ color = 'black' }) => color};
    border-radius: 8px;
    flex-grow: 1;
`;

const SortableItem = sortableElement(({
    question,
    setActiveQuestionId,
    activeQuestionId,
    setFieldValue,
    values,
    _index
}) => {
    const [showCardDragIcon, setShowCardDragIcon] = useState(false);
    const [activeOptionId, setActiveOptionId] = useState(null);
    const idQuestion = get(question, 'idQuestion');
    const isActiveQuestion = idQuestion === activeQuestionId;
    const idTypeQuestion = get(question, 'idTypeQuestion');
    const [switchConditionalQuestion, setSwitchConditionalQuestion] = useState(size(get(question, 'conditions')) > 0);
    const htmlQuestionId = 'html_question_id';
    const questions = get(values, 'questions');
    const isOpen = get(question, 'idTypeQuestion') === typeQuestions.OPEN;
    const isMultiple = get(question, 'idTypeQuestion') === typeQuestions.MULTIPLE;
    const npsOptions = times(10, (i) => ({ label: i + 1, value: i + 1 }));

    const getTypeQuestionSelected = () => find(typeQuestionOptions, { value: idTypeQuestion });

    const updateQuestions = (key, value) => {
        const questionsUpdated = map(questions, (item) => {
            return get(item, 'idQuestion') === idQuestion
            ? { ...item, [key]: value, ...(key === 'idTypeQuestion' ? { conditions: [], options: [] } : {}) }
            : item;
        });

        setFieldValue('questions', questionsUpdated);
    };

    const removeQuestion = () => {
        const questionsUpdated = filter(questions, (item) => idQuestion !== get(item, 'idQuestion'));
        setFieldValue('questions', questionsUpdated);
    };

    const duplicateQuestion = () => {
        const questionsUpdated = flatMap(questions, (question) => {
            if (idQuestion === get(question, 'idQuestion')) {
                return [question, { ...first(addUUID([question], true)), idQuestion: genUUID() }]
            }

            return question;
        });

        setFieldValue('questions', questionsUpdated);
    };

    const RenderOptions = () => {
        switch (idTypeQuestion) {
            case 5:
                return <FormInput placeholder='Texto de respuesta' disabled style={{ backgroundColor: '#F4F4F4', width: 363 }} />;
            case 3:
                return <Nps npsOptions={npsOptions} updateQuestions={updateQuestions} question={question} />;
            case 2:
                return <Row gap={15} paddingVertical={25}>
                    {map(satisfactionOptions, ({ name, color, translate }) => {
                        return <WrapperIcon color={color} justify='center' align='center' height={70} width={104}>
                            <Tooltip placement='top' title={translate} key={name} autoAdjustOverflow={false}>
                                <Column justify='center' align='center'>
                                    <a><Icon name={name} color={color} size={30} /></a>
                                    <Text text={translate} strong color={color} />
                                </Column>
                            </Tooltip>
                        </WrapperIcon>;
                    })}
                </Row>;
            case 1:
            case 4:
                return <MultipleOptions
                    isActiveQuestion={isActiveQuestion}
                    question={question}
                    updateQuestions={updateQuestions}
                    activeOptionId={activeOptionId}
                    setActiveOptionId={setActiveOptionId}
                    typeQuestion={idTypeQuestion}
                    limitSelect={get(question, 'limitSelect')}
                    setFieldValue={setFieldValue}
                />;
        }
    };

    const allowConditionalQuestion = () => {
        const positionsWithBlockedConditions = [size(questions) - 1, size(questions) - 2];
        const indexFound = findIndex(questions, { idQuestion });
        return (size(questions) > 2 && !includes(positionsWithBlockedConditions, indexFound)) && (size(get(question, 'options')) > 1 || idTypeQuestion === 2);
    };

    const questionHasAssociation = () => {
        const hasParentAssociation = some(questions, ({ conditions }) => {
            return includes(map(conditions, 'idNextQuestion'), idQuestion);
        });

        return hasParentAssociation || size(get(question, 'conditions')) > 0;
    };

    const getParentsAssociation = () => {
        let parents = [];

        map(questions, (question) => {
            if (some(get(question, 'conditions'), { idNextQuestion: idQuestion })) {
                parents = [...parents, ...[get(question, 'orden')]];
            };
        });

        return parents;
    };

    const showToolTip = () => size(getParentsAssociation()) > 0 ? {} : { visible: false };

    return <Row
        pT={0}
        pB={15}
        paddingHorizontal={15}
        mB={10}
        backgroundColor='#F4F4F4'
        borderRadius={6}
        position='static'
        zIndex={1000}
        boxShadow='0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%) '
        onMouseOver={() => setShowCardDragIcon(true)}
        onMouseLeave={() => setShowCardDragIcon(false)}
        borderLeft={isActiveQuestion ? '6px solid rgb(31, 160, 46)' : ''}
        id={htmlQuestionId}
        onClick={({ target }) => {
            target.id === htmlQuestionId && setActiveOptionId(null);
            setActiveQuestionId(idQuestion);
        }}
    >
        <Column move>
            <Row justify='center' id={htmlQuestionId}>
                <Icon name='drag-horizontal' color={`${showCardDragIcon || isActiveQuestion ? '#1fa02e' : '#F4F4F4'}`} size={20} />
            </Row>
            <Row justify='space-between' id={htmlQuestionId}>
                <Text text={`${_index + 1}. ${get(getTypeQuestionSelected(), 'label')}`} strong color='#1fa02e' size={14} />
                {questionHasAssociation() && <Tooltip placement='top' title={getParentsAssociation()} {...showToolTip()}>
                    <a><Icon name='link' color='#1fa02e' size={20} /></a>
                </Tooltip>}
            </Row>
            <Row onClick={() => setActiveOptionId(null)} gap={5}>
                <Row style={{ flexGrow: 1.5 }}>
                    <FormInput
                        value={get(question, 'question')}
                        placeholder='Ingresar pregunta'
                        label={`Pregunta ${_index + 1}`}
                        onChange={({ target }) => updateQuestions('question', get(target, 'value'))}
                        textArea
                        heightTextArea={34}
                    />
                </Row>
                <Row style={{ flexGrow: 1 }}>
                    <FormSelect
                        label='Tipo de pregunta'
                        options={typeQuestionOptions}
                        value={getTypeQuestionSelected()}
                        onChange={({ value }) => updateQuestions('idTypeQuestion', get(value, 'value'))}
                    />
                </Row>
            </Row>
            {RenderOptions()}
            <Divider id={htmlQuestionId} style={{ marginTop: 10, marginBottom: 15 }} />
            <Row justify='flex-end' gap={15} id={htmlQuestionId}>
                {isActiveQuestion && !isOpen && !isMultiple && <Row justify='flex-end' align='center' mR={60} gap={5}>
                    <Text text='Condiciones' strong />
                    <Switch checked={switchConditionalQuestion} onChange={(value) => setSwitchConditionalQuestion(value)} disabled={!allowConditionalQuestion()} />
                </Row>}
                <Icon name='duplicate' color='#1fa02e' size={23} pointer onClick={duplicateQuestion} />
                <Icon name='new-trash' color='#1fa02e' size={23} pointer onClick={removeQuestion} />
            </Row>
            {switchConditionalQuestion && !isOpen && <ConditionalQuestion values={values} question={question} setFieldValue={setFieldValue} updateQuestions={updateQuestions} typeQuestion={idTypeQuestion} satisfactionOptions={satisfactionOptions} />}
        </Column>
    </Row>;
});

const Question = ({ index, setActiveQuestionId, activeQuestionId, setFieldValue, question, values }) => {
    return <SortableItem
        index={index}
        _index={index}
        question={question}
        setActiveQuestionId={setActiveQuestionId}
        activeQuestionId={activeQuestionId}
        setFieldValue={setFieldValue}
        values={values}
    />;
};

Question.propTypes = {
    index: number,
    question: object,
    setActiveQuestionId: func,
    activeQuestionId: number,
    setFieldValue: func,
    values: object
};

export default Question;
